import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { NgLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, Observable, of, startWith, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { LastAndFirstNamePipe } from '../../common/pipes/last-and-first-name/last-and-first-name.pipe';
import { SsoUserDetailsDto } from '../../common/interfaces/sso-user-details-dto';
import { AccountsSearchService } from '../../common/services/accounts-search.service';
import { PaginatedSearchResponse } from '../../common/interfaces/paginated-search-response';
import { AccountsFilterDto } from '../../common/interfaces/accounts-filter-dto';

@Component({
    selector: 'lib-audit-report-user-select',
    imports: [
        AsyncPipe,
        LastAndFirstNamePipe,
        NgSelectComponent,
        FormsModule,
        ReactiveFormsModule,
        NgLabelTemplateDirective,
        NgOptionTemplateDirective,
        NgClass,
        NgIf
    ],
    templateUrl: './audit-report-user-select.component.html',
    styleUrl: './audit-report-user-select.component.scss'
})
export class AuditReportUserSelectComponent implements OnInit, OnDestroy {
    private readonly accountSearchService = inject(AccountsSearchService);
    private readonly toastrService = inject(ToastrService);

    @Input() enterpriseId!: number;
    @Input() placeholder = 'User Search...';
    @Input() multiple = false;
    @Output() usersSelectedEvent = new EventEmitter<SsoUserDetailsDto[] | SsoUserDetailsDto>();
    @ViewChild(NgSelectComponent, { static: true }) ngSelect!: NgSelectComponent;

    currentSearchTerm = '';
    selectedUsers: SsoUserDetailsDto[] | SsoUserDetailsDto | undefined;
    usersSearchInput$ = new Subject<string>();
    users$: Observable<(SsoUserDetailsDto & { label: string })[]> = of([]);
    usersLoading = false;
    sortBy = 'lastName,firstName';
    sortOrder = 'ASC';

    protected destroyed$: Subject<void> = new Subject<void>();

    ngOnInit() {
        this.loadUsers();
        outputToObservable(this.ngSelect.changeEvent)
            .pipe(takeUntil(this.destroyed$))
            .subscribe($e => this.onValueChange($e));
    }

    loadUsers(): void {
        this.users$ = this.usersSearchInput$.pipe(
            startWith(''),
            filter(searchTerm => searchTerm?.length === 0 || searchTerm?.length >= 3),
            distinctUntilChanged(),
            debounceTime(200),
            tap(() => this.usersLoading = this.multiple),
            switchMap(searchTerm => this.getUsers(searchTerm).pipe(
                map(response =>
                    response.content.map(user => ({
                        ...user,
                        label: this.getUserLabel(user)
                    }))
                ),
                catchError(() => {
                    this.toastrService.error('Failed to retrieve users.', 'Error');
                    return of([]);
                }),
                finalize(() => this.usersLoading = false)
            ))
        );
    }

    private getUsers(searchTerm = ''): Observable<PaginatedSearchResponse<SsoUserDetailsDto>> {
        const excludedIds = Array.isArray(this.selectedUsers) && this.multiple
            ? this.selectedUsers.map(u => u.id)
            : [];
        const accountFilter = {
            searchTerm: searchTerm,
            isEnterpriseAdmin: true,
            page: 0,
            itemsPerPage: 5,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder,
            excludedUserIds: excludedIds
        } as AccountsFilterDto;
        return this.accountSearchService.search(this.enterpriseId, accountFilter);
    }

    onValueChange(value: SsoUserDetailsDto | SsoUserDetailsDto[]): void {
        this.ngSelect.close();
        this.usersSelectedEvent.emit(value);
    }

    resetCurrentSearch(): void {
        this.selectedUsers = this.multiple ? [] : undefined;
        this.usersSelectedEvent.emit(this.selectedUsers);
        this.usersSearchInput$.next('');
        this.ngSelect.close();
    }

    onSearchTermChange(event: { term: string; items: SsoUserDetailsDto[] }): void {
        if (this.currentSearchTerm !== event.term) {
            this.currentSearchTerm = event.term;
            if (!this.multiple) {
                this.selectedUsers = undefined;
            }
            this.usersSearchInput$.next(event.term);
        }
    }

    onDropdownOpen() {
        this.usersSearchInput$.next('');
    }

    onDropdownClose() {
        if (this.selectedUsers === undefined) {
            this.resetCurrentSearch();
        }
    }

    onClear(item: Partial<SsoUserDetailsDto>): void {
        this.ngSelect.clearItem(item);
    }

    compareWith(input: SsoUserDetailsDto, selected: SsoUserDetailsDto): boolean {
        return input.id === selected.id;
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    getUserLabel(user: SsoUserDetailsDto): string {
        return user ? `${user.lastName}, ${user.firstName} (${user.username})` : '';
    }
}
